import { Dispatch, SetStateAction, use } from 'react'

import { ChatContext } from '@app/context'
import { useConversation, useUser } from '@app/hooks'
import { useChatSearchParams } from '@app/hooks/useChatState/useChatSearchParams'

import type { ConversationItem } from '@app/models'
import { scrollToBottom } from '@app/utils'

import type { ReplyAttachment } from './types'
import {
  handleChange as replyHandleChange,
  handleSubmit as replyHandleSubmit,
} from './useReplyHandlers'
import { useSendMessage } from './useSendMessage'
import type { FollowUpActionKind } from '../Dialog/FollowUpActions/types'

const useReply = (
  inputValue: Map<number, string>,
  setInputValue: Dispatch<SetStateAction<Map<number, string>>>,
  attachments: Map<number, ReplyAttachment[]>,
  setAttachments: Dispatch<SetStateAction<Map<number, ReplyAttachment[]>>>,
  isDeveloper: boolean,
  activeFollowUpAction: FollowUpActionKind | null
) => {
  const conversation: ConversationItem | null = useConversation()
  const { dispatch, state } = use(ChatContext)

  const { utmTags } = useChatSearchParams()

  const { currentUser } = useUser()
  const handleSendMessage = useSendMessage({
    conversation,
    currentUser,
    dispatch,
    state,
    utmTags,
    isDeveloper,
    activeFollowUpAction,
  })

  const noopSubmit: ReturnType<typeof replyHandleSubmit> = (event) => {
    event.preventDefault()
    return false
  }
  const noopChange: ReturnType<typeof replyHandleChange> = () => {}

  const handleSubmit = (conversationId: number) => {
    if (!conversation) {
      return noopSubmit
    }

    return replyHandleSubmit(
      conversationId,
      conversation,
      inputValue,
      setInputValue,
      handleSendMessage,
      attachments,
      setAttachments
    )
  }

  const handleChange = (conversationId: number) => {
    if (!conversation) {
      return noopChange
    }

    return replyHandleChange(conversationId, setInputValue)
  }

  return {
    inputValue,
    handleSubmit,
    handleChange,
    handleSendMessage,
    scrollToBottom,
  }
}

export default useReply
